import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Snackbar,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import dataQualityService from '../../services/dataQualityService';

const EntityDetails = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [entityType, setEntityType] = useState('all');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [entities, setEntities] = useState([]);
  const [entityQuality, setEntityQuality] = useState(null);
  const [entityIssues, setEntityIssues] = useState([]);

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (entityType !== 'all') filters.type = entityType;
      if (searchQuery) filters.search = searchQuery;
      const data = await dataQualityService.getEntities(filters);
      const entityList = (data?.entities ?? data ?? []).map(e => ({
        id: e.entityId || e.id || e.entity_id,
        name: e.name,
        type: e.entityType || e.type || e.entity_type,
        qualityScore: Math.round(parseFloat(e.qualityScore || e.quality_score || e.avg_score || 0)),
        completeness: Math.round(parseFloat(e.dimensions?.coverage || e.completeness || 0)),
        temporal: Math.round(parseFloat(e.dimensions?.temporal || e.temporal || 0)),
        geographical: Math.round(parseFloat(e.dimensions?.geographical || e.geographical || 0)),
        technological: Math.round(parseFloat(e.dimensions?.technological || e.technological || 0)),
        reliability: Math.round(parseFloat(e.dimensions?.reliability || e.reliability || 0)),
        lastUpdated: e.assessedAt || e.last_assessed || e.updated_at || new Date().toISOString(),
        assessmentCount: parseInt(e.assessment_count || 1),
        issueCount: parseInt(e.issue_count || e.error_count || 0),
        status: e.verificationStatus || e.verification_type || 'Unevaluated'
      }));
      setEntities(entityList);
    } catch (err) {
      console.error('Error fetching entities:', err);
      setSnackbar({ open: true, message: `Failed to load entities: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [entityType, searchQuery]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const getQualityColor = (score) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getQualityIcon = (score) => {
    if (score >= 80) return <CheckCircleIcon />;
    if (score >= 60) return <WarningIcon />;
    return <ErrorIcon />;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const filteredEntities = entities.filter(entity => {
    const matchesSearch = entity.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = entityType === 'all' || entity.type === entityType;
    const matchesQuality = qualityFilter === 'all' ||
                          (qualityFilter === 'high' && entity.qualityScore >= 80) ||
                          (qualityFilter === 'medium' && entity.qualityScore >= 60 && entity.qualityScore < 80) ||
                          (qualityFilter === 'low' && entity.qualityScore < 60);
    return matchesSearch && matchesType && matchesQuality;
  });

  const handleViewEntity = async (entity) => {
    setSelectedEntity(entity);
    setDialogOpen(true);
    try {
      const [quality, issues] = await Promise.all([
        dataQualityService.getEntityQuality(entity.type, entity.id).catch(() => null),
        dataQualityService.getEntityIssues(entity.type, entity.id).catch(() => [])
      ]);
      setEntityQuality(quality);
      setEntityIssues(issues?.issues ?? issues ?? []);
    } catch (err) {
      console.error('Error fetching entity details:', err);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEntity(null);
    setEntityQuality(null);
    setEntityIssues([]);
  };

  const renderQualityMetrics = (entity) => {
    const metrics = entityQuality || entity;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color={getQualityColor(metrics.qualityScore)}>
              {metrics.qualityScore}%
            </Typography>
            <Typography variant="body2" color="text.secondary">Overall Score</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={10}>
          <Grid container spacing={2}>
            {[
              { label: 'Temporal', value: metrics.temporal },
              { label: 'Geographical', value: metrics.geographical },
              { label: 'Technological', value: metrics.technological },
              { label: 'Reliability', value: metrics.reliability },
              { label: 'Completeness', value: metrics.completeness }
            ].map((m) => (
              <Grid item xs={6} md={2.4} key={m.label}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color={getQualityColor(m.value)}>{m.value}%</Typography>
                  <Typography variant="body2" color="text.secondary">{m.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Loading entity details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Entity Quality</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchEntities} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search entities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select value={entityType} onChange={(e) => setEntityType(e.target.value)} label="Entity Type">
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="organization">Organization</MenuItem>
            <MenuItem value="facility">Facility</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Quality Level</InputLabel>
          <Select value={qualityFilter} onChange={(e) => setQualityFilter(e.target.value)} label="Quality Level">
            <MenuItem value="all">All Levels</MenuItem>
            <MenuItem value="high">High (80%+)</MenuItem>
            <MenuItem value="medium">Medium (60-79%)</MenuItem>
            <MenuItem value="low">Low (&lt;60%)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">TOTAL ENTITIES</Typography>
            <Typography variant="h4">{filteredEntities.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">HIGH QUALITY</Typography>
            <Typography variant="h4" color="success.main">{filteredEntities.filter(e => e.qualityScore >= 80).length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">NEEDS REVIEW</Typography>
            <Typography variant="h4" color="warning.main">{filteredEntities.filter(e => e.qualityScore < 60).length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">TOTAL ISSUES</Typography>
            <Typography variant="h4" color="error.main">{filteredEntities.reduce((sum, e) => sum + e.issueCount, 0)}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Entity Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Quality Score</TableCell>
                <TableCell>Assessments</TableCell>
                <TableCell>Issues</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Assessed</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntities
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entity) => (
                  <TableRow key={`${entity.type}-${entity.id}`} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {entity.type === 'organization' ? <BusinessIcon sx={{ mr: 1 }} /> : <LocationOnIcon sx={{ mr: 1 }} />}
                        <Typography variant="body2" fontWeight="medium">{entity.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Chip label={entity.type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getQualityIcon(entity.qualityScore)}
                        <Typography variant="body2" sx={{ ml: 1 }} color={getQualityColor(entity.qualityScore)}>{entity.qualityScore}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{entity.assessmentCount}</TableCell>
                    <TableCell>
                      {entity.issueCount > 0 ? (
                        <Badge badgeContent={entity.issueCount} color="error"><ErrorIcon /></Badge>
                      ) : (
                        <CheckCircleIcon color="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={entity.status} size="small" color={entity.status === 'Certified' ? 'success' : entity.status === 'Trusted' ? 'primary' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>{new Date(entity.lastUpdated).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewEntity(entity)}><VisibilityIcon /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredEntities.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedEntity?.type === 'organization' ? <BusinessIcon sx={{ mr: 1 }} /> : <LocationOnIcon sx={{ mr: 1 }} />}
            {selectedEntity?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEntity && (
            <Box sx={{ pt: 2 }}>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Quality Metrics (PACT DQI)</Typography>
                {renderQualityMetrics(selectedEntity)}
              </Paper>

              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Entity Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>{selectedEntity.type}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Verification Status</Typography>
                    <Typography variant="body1" fontWeight="medium">{selectedEntity.status}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Assessments</Typography>
                    <Typography variant="body1" fontWeight="medium">{selectedEntity.assessmentCount}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Last Assessed</Typography>
                    <Typography variant="body1" fontWeight="medium">{new Date(selectedEntity.lastUpdated).toLocaleString()}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {entityIssues.length > 0 && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Quality Issues ({entityIssues.length})</Typography>
                  <List>
                    {entityIssues.map((issue, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <ErrorIcon sx={{ color: getSeverityColor(issue.severity || 'medium') }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={issue.rule_name || issue.rule || 'Quality Issue'}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {issue.field && `Field: ${issue.field}`}
                                </Typography>
                                <Typography variant="body2">{issue.message || issue.description || 'Failed quality check'}</Typography>
                              </Box>
                            }
                          />
                          <Chip
                            label={issue.severity || 'medium'}
                            size="small"
                            sx={{ color: getSeverityColor(issue.severity || 'medium'), borderColor: getSeverityColor(issue.severity || 'medium') }}
                            variant="outlined"
                          />
                        </ListItem>
                        {index < entityIssues.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default EntityDetails;
